/**
 * sysData — the read-only view on internalData
 *
 * What the library knows about the running system, right now. The read-only
 * counterpart to appData:
 *
 *   appData  — the app's own state, persisted with the project, read/write
 *   sysData  — the library's state, never persisted, read-only
 *
 * This module holds **no state**. Everything it reports lives in `internalData`
 * (store/store.ts): the lifecycle flags as plain schema keys, and
 * `phase`/`projectName`/`projectId`/`projectDirty` as derived keys off what is
 * already there. sysData is a curated, read-only projection of a subset of it,
 * so there is one internal store rather than two that drift apart.
 *
 * There is no subscription machinery here either — subscribing to a sysData
 * field is subscribing to that key on internalData, and `_setSysData` is one
 * `internalData.batch()` so a patch of several fields arrives as one change.
 *
 *   sysData.subscribe()   — react to one field, several, or the whole thing
 *   bindSysData()         — the same, wired onto an element's attrs and
 *                           methods (see components/base.ts)
 *   app.sysData.busy      — plain read, no subscription
 *
 * Writes go through `_setSysData`, which is deliberately NOT re-exported from
 * exports.ts; an app reads through the `app.sysData` proxy, which refuses
 * writes. What fills it is mostly a projection of events that already exist —
 * `initSysData()` listens to the configurator/step/project events the
 * library has always dispatched, and the busy mixin writes `busy` itself.
 * Two facts have no event carrying them: the Paramate session id and resolved
 * tier (<as-configurator> writes those).
 * projectSaving is written directly by saveCurrentProject, since it covers
 * only real saves while as-before-project-saved fires for autosaves too. The
 * two auth flags are written by getUserData (features/serverApi/asApi.ts) the same way.
 *
 * A project reset leaves auth alone (see RESETTABLE): signing in outlives the
 * project the app happens to have open.
 */

import { internalData } from './store.ts';
import type { Deployment } from '../data/constants.ts';
import type { SysPhase, StepSelectorState } from './store.ts';

// Re-exported so the public API keeps its home here, even though the schema
// that needs them has to live alongside internalData.
export type { SysPhase, StepSelectorState };

/**
 * The shape of the view. Every field is a key on `internalData` — plain or
 * derived — so this is a curated subset of that store rather than a
 * description of separate state.
 */
export interface SysData {
  // Lifecycle ================================================================
  /** <as-app> has finished its init queue. */
  appReady: boolean;
  /** The Paramate session is live (set on initialized, cleared on a reinit or
   *  a lost session). */
  configuratorReady: boolean;
  /** A CAD rebuild is in flight — between before-configure and after-configure. */
  configuring: boolean;
  /** A saved session is being restored onto the server. */
  sessionRestoring: boolean;
  /** The server session died (expiry, 404). Cleared when the next one initialises. */
  sessionLost: boolean;
  /** Between `as-before-project-loaded` and `as-after-project-loaded`. Cleared
   *  even when the load throws — see _loadProject's finally in project-mixin. */
  projectLoading: boolean;
  /** A real file save is running (not the debounced autosave). */
  projectSaving: boolean;
  /** The aggregate ref-counted busy state — same answer as `as-app-busy`. */
  busy: boolean;
  /** Which keys are holding it busy. */
  busyKeys: string[];
  /** An export is in flight — between an `as-export-status` 'pending' and its
   *  'success' / 'error' / 'cancelled'. Any exporter counts. */
  exporting: boolean;
  /** Derived — all of the above rolled into one label. See SysPhase. */
  phase: SysPhase;

  // Auth =====================================================================
  /** The user is signed in. False until the first answer lands, and false when
   *  the check never got one (see getUserData's transportFailed). */
  isAuthenticated: boolean;
  /** A getUserData() call is in flight. True for the check at boot and again on
   *  every keepalive refetch, so it is not the same thing as the first-paint
   *  gate `<as-app auth-checking>`, which covers the boot check alone. */
  pendingAuth: boolean;

  // Identity =================================================================
  /** The Paramate session id. undefined until the configurator initialises.
   *  Same value <as-configurator>.getSystemInfo() reports. */
  sessionId: string | undefined;
  /** Which Paramate tier the configurator resolved to. */
  tier: string | undefined;
  /** Where the suite is installed: 'cloud', or 'onprem' on a customer's own
   *  server with no trinckle services. Known from the start, never detected. */
  deployment: Deployment;
  /** <as-app app-id>. */
  appId: string | undefined;
  /** <as-app app-version> — the app's own version, if it set one. */
  appVersion: string | undefined;
  /** The as-components version. */
  componentsVersion: string | undefined;

  // Current step =============================================================
  /** The active step, or undefined in an app with no <as-step-selector> —
   *  plenty of apps are single-screen. Check the object for undefined rather
   *  than treating index 0 or an empty id as "no step". */
  stepSelector: StepSelectorState | undefined;

  // Current project ==========================================================
  /** Derived from `internalData.currentProject`. */
  projectName: string | undefined;
  /** Derived from `internalData.currentProject`. */
  projectId: string | undefined;
  /** Derived from `internalData.unsavedChanges`. */
  projectDirty: boolean;

  // History ==================================================================
  /** Something to undo. Same answer as `as-history-changed`'s `canUndo`. */
  canUndo: boolean;
  /** Something to redo. */
  canRedo: boolean;
}

export type SysDataKey = keyof SysData;

/** Every key the view exposes, in the order `print()` and a spread show them.
 *  Each is a key on internalData, plain or derived, so a read is a straight
 *  pass-through with no mapping and no second copy. */
const SYS_KEYS: readonly SysDataKey[] = [
  'appReady', 'configuratorReady', 'configuring', 'sessionRestoring', 'sessionLost',
  'projectLoading', 'projectSaving', 'busy', 'busyKeys', 'exporting', 'phase',
  'isAuthenticated', 'pendingAuth',
  'sessionId', 'tier', 'deployment', 'appId', 'appVersion', 'componentsVersion',
  'stepSelector',
  'projectName', 'projectId', 'projectDirty',
  'canUndo', 'canRedo',
] as const;

/** Computed by internalData from the fields above. _setSysData drops a write to
 *  one rather than letting it look like it worked and then not. */
const DERIVED_KEYS: ReadonlySet<string> = new Set(['phase', 'projectName', 'projectId', 'projectDirty']);

/** SYS_KEYS as a Set, for the whole-state subscriber's "was that one of mine?"
 *  check on every internalData commit. */
const SYS_KEY_SET: ReadonlySet<string> = new Set(SYS_KEYS as readonly string[]);

/** What a reset puts back — only the fields sysData owns. `appId` and
 *  `currentProject` belong to the rest of the app and are left alone. */
const RESETTABLE: Partial<SysData> = {
  appReady: false,
  configuratorReady: false,
  configuring: false,
  sessionRestoring: false,
  sessionLost: false,
  projectLoading: false,
  projectSaving: false,
  busy: false,
  busyKeys: [],
  exporting: false,
  canUndo: false,
  canRedo: false,
  sessionId: undefined,
  tier: undefined,
  appVersion: undefined,
  componentsVersion: undefined,
  stepSelector: undefined,
};

const _read = (key: SysDataKey): any => (internalData as any)[key];

// ============================================================================
// Reading
// ============================================================================

/** A snapshot of the current system state. A fresh plain object each call, so
 *  one held across a change still reads as it did when it was taken. */
export function getSysData(): Readonly<SysData> {
  const out: any = {};
  for (const key of SYS_KEYS) out[key] = _read(key);
  return out as SysData;
}

// ============================================================================
// The `sysData` namespace — read it and react to it
// ============================================================================

/**
 * Ergonomic wrapper around sysData reads, matching `appData`'s shape so the two
 * feel the same at a call site. Every field of `SysData` is readable directly;
 * `subscribe`/`subscribeAll`/`snapshot`/`print` are the reserved names.
 *
 * There is no `patch`, no setter and no `defaults`: sysData reports what the
 * library is doing, so the way to change it is to make the library do something
 * else. Writes are ignored (with a one-off warning) rather than throwing — a
 * stray assignment shouldn't take an app down in production.
 */
export type SysDataAPI = Readonly<SysData> & {
  /**
   * React to one field, several, or an alias object. Does NOT fire immediately
   * — use `bindSysData()` on an AsEl when you want the value applied straight
   * away. Fires once per write however many of the named fields moved together.
   */
  subscribe<K extends SysDataKey>(key: K, cb: (value: SysData[K], key: K) => void): () => void;
  subscribe<K extends SysDataKey>(keys: readonly K[], cb: (values: Array<SysData[K]>, keys: readonly K[]) => void): () => void;
  subscribe<T extends Record<string, SysDataKey>>(aliases: T, cb: (values: { [A in keyof T]: SysData[T[A]] }) => void): () => void;
  /** React to any sysData change, with the whole thing. Does NOT fire
   *  immediately. A change to an internalData field this view doesn't expose
   *  (userData, asset paths) is not a sysData change and won't wake it. */
  subscribeAll(cb: (sys: Readonly<SysData>) => void): () => void;
  /** A plain, detached copy — safe to hold across a change. */
  snapshot(): Readonly<SysData>;
  /** console.log the snapshot — `sysData` on its own prints as a Proxy, which
   *  is unreadable in devtools. */
  print(): void;
};

/** Keys already warned about, so a write in a render loop doesn't flood the
 *  console. Once per key per page, same budget as `warnDeprecated`. */
const _warnedKeys = new Set<string>();

function _warnReadOnly(key: string): void {
  if (_warnedKeys.has(key)) return;
  _warnedKeys.add(key);
  console.warn(
    `[SYSDATA] sysData.${key} is read-only — the write was ignored. sysData reports what the library is doing; ` +
    `to change it, change what the library is doing. For your own state, use appData.`,
  );
}

const RESERVED = ['subscribe', 'subscribeAll', 'snapshot', 'print'] as const;

/** Overloads can't live in an object literal, and this one is a Proxy anyway. */
function _subscribe(target: any, cb: any): () => void {
  return Array.isArray(target) ? internalData.subscribeKeys(target, cb)
    : typeof target === 'object' && target !== null ? _subscribeAliases(target, cb)
    : internalData.subscribeKey(target, cb);
}

/** { big: 'busy' } -> cb({ big: true }). The alias form params has, for the
 *  same reason: a call site reads better naming its own fields. */
function _subscribeAliases(aliases: Record<string, SysDataKey>, cb: (values: any) => void): () => void {
  const entries = Object.entries(aliases);
  return internalData.subscribeKeys(entries.map(([, key]) => key), () => {
    const out: Record<string, any> = {};
    for (const [alias, key] of entries) out[alias] = _read(key);
    cb(out);
  });
}

/**
 * What the library is doing, right now — read-only.
 *
 *   sysData.busy                                  // plain read, no subscription
 *   sysData.phase                                 // all the flags in one label
 *   sysData.subscribe('busy', (busy) => ...)      // react to one
 *   sysData.subscribe(['busy', 'phase'], cb)      // react to several
 *   sysData.subscribeAll((sys) => ...)            // react to any change
 *
 * The same object `<as-app>.sysData` hands you — there is one sysData, and it
 * doesn't need the element. The same `subscribe` / `subscribeAll` pair is on
 * `appData` and `params`.
 *
 * The Proxy target is a dummy: every read resolves through `internalData`, so a
 * reference held across a change never goes stale.
 */
export const sysData: SysDataAPI = new Proxy({} as SysDataAPI, {
  get(_target, prop: string | symbol) {
    if (typeof prop === 'string') {
      switch (prop) {
        case 'subscribe':    return _subscribe;
        case 'subscribeAll': return (cb: (sys: Readonly<SysData>) => void) =>
          internalData.subscribeAll((_store: any, changedKeys: ReadonlySet<string>) => {
            // internalData carries plenty this view doesn't expose (auth, asset
            // paths, the icon list); a change to one of those is not a sysData
            // change.
            for (const key of changedKeys) {
              if (SYS_KEY_SET.has(key)) { cb(getSysData()); return; }
            }
          });
        case 'snapshot':     return () => getSysData();
        case 'print':        return () => console.log(getSysData());
      }
    }
    // Only what this view declares. internalData carries plenty more — auth,
    // asset paths, the icon list — and reading it through here would make
    // sysData a second door onto the whole internal store.
    return SYS_KEY_SET.has(prop as string) ? _read(prop as SysDataKey) : undefined;
  },
  set(_target, prop: string | symbol) {
    _warnReadOnly(String(prop));
    return true;   // swallow it — a throw here would be a hard crash in strict mode
  },
  deleteProperty(_target, prop: string | symbol) {
    _warnReadOnly(String(prop));
    return true;
  },
  // Without these, spreading or Object.keys() on the proxy sees the empty
  // dummy target rather than the live state.
  has(_target, prop: string | symbol) {
    return (RESERVED as readonly string[]).includes(prop as string) || SYS_KEY_SET.has(prop as string);
  },
  ownKeys() {
    return [...SYS_KEYS] as string[];
  },
  getOwnPropertyDescriptor(_target, prop: string | symbol) {
    if (!SYS_KEY_SET.has(prop as string)) return undefined;
    return { value: _read(prop as SysDataKey), enumerable: true, configurable: true, writable: false };
  },
});

// ============================================================================
// Writing (library-internal — not exported from exports.ts)
// ============================================================================

/** The two non-primitive fields get a fresh object on every write, so identity
 *  always differs — compare contents instead, or every busy toggle looks like a
 *  change. internalData's setter does not dedupe; this is what stops the
 *  redundant notification. */
function _changed(key: SysDataKey, next: any): boolean {
  if (key === 'busyKeys') {
    // Order doesn't matter: it comes from a Set.
    const before = (_read('busyKeys') ?? []) as string[];
    const after = (next ?? []) as string[];
    return before.length !== after.length || after.some(k => !before.includes(k));
  }
  if (key === 'stepSelector') {
    const before = _read('stepSelector') as StepSelectorState | undefined;
    const after = next as StepSelectorState | undefined;
    if (!before || !after) return before !== after;
    return before.id !== after.id || before.index !== after.index || before.count !== after.count;
  }
  return _read(key) !== next;
}

/**
 * The only writer. Applies a patch to internalData inside one batch, so the
 * several fields it touches — plus any derived key they move — reach
 * subscribers as a single change rather than one each.
 *
 * Derived keys in the patch are ignored: they are computed from the rest, so
 * writing one would appear to work and then not.
 */
export function _setSysData(patch: Partial<SysData>): void {
  internalData.batch(() => {
    for (const [key, value] of Object.entries(patch) as Array<[SysDataKey, any]>) {
      if (DERIVED_KEYS.has(key)) continue;
      if (!_changed(key, value)) continue;
      (internalData as any)[key] = value;
    }
  });
}

/** Back to the starting state, subscribers kept. For tests, and for a teardown
 *  that removes <as-app> without replacing it. */
export function resetSysData(): void {
  _setSysData(RESETTABLE);
}

// ============================================================================
// Wiring
// ============================================================================

let _teardown: (() => void) | null = null;

/**
 * Point the store at the running app. Listens to the events that already carry
 * this information and writes what it hears into internalData.
 *
 * Idempotent: calling it again re-points at `appEl` (the project events are
 * dispatched on the as-app element, not window, so the element matters).
 * Returns a teardown, which <as-app> calls on disconnect.
 */
export function initSysData(appEl: HTMLElement): () => void {
  _teardown?.();

  // configuratorReady and the session flags are written by <as-configurator>
  // itself when a session starts (see _sessionStarted there): no event carries
  // that moment on every SDK build, and the element is the one that knows.
  const onBeforeConfigure = () => _setSysData({ configuring: true });
  const onAfterConfigure = () => _setSysData({ configuring: false });
  const onAfterRestore = () => _setSysData({ sessionRestoring: false });
  const onRestoreFailed = () => _setSysData({ sessionRestoring: false });
  const onSessionLost = () => _setSysData({ sessionLost: true, configuratorReady: false });

  // Bubbles and is composed, so it reaches window from anywhere in the app.
  // getTotalSteps() isn't in the detail, so it comes off the selector itself.
  const onStepChanged = (e: Event) => {
    const detail = (e as CustomEvent<{ currentStepId?: string; currentStepIndex?: number }>).detail;
    if (!detail) return;
    _setSysData({
      stepSelector: {
        id: detail.currentStepId ?? '',
        index: detail.currentStepIndex ?? -1,
        count: (e.target as any)?.getTotalSteps?.() ?? 0,
      },
    });
  };

  // loadProject also sets projectLoading directly, on both sides. Both, on
  // purpose: the direct writes make the flag right with no wiring at all (and
  // its finally clears it even when the load throws and the after event never
  // fires), while these listeners cover a host that drives its own load and
  // dispatches the events itself. _setSysData ignores a write that changes
  // nothing, so the overlap costs nothing.
  const onBeforeProjectLoaded = () => _setSysData({ projectLoading: true });
  const onAfterProjectLoaded = () => _setSysData({ projectLoading: false });

  const onHistoryChanged = (e: Event) => {
    const { canUndo, canRedo } = (e as CustomEvent<{ canUndo?: boolean; canRedo?: boolean }>).detail ?? {};
    _setSysData({ canUndo: !!canUndo, canRedo: !!canRedo });
  };

  // Several exporters can run at once (each names itself in exporterId), so
  // count them rather than flip a flag on the last event seen.
  const pendingExports = new Set<string>();
  const onExportStatus = (e: Event) => {
    const { exporterId, state } = (e as CustomEvent<{ exporterId?: string; state?: string }>).detail ?? {};
    const id = exporterId ?? 'default';
    if (state === 'pending') pendingExports.add(id);
    else pendingExports.delete(id);
    _setSysData({ exporting: pendingExports.size > 0 });
  };

  window.addEventListener('paramate-configurator-before-configure', onBeforeConfigure);
  window.addEventListener('paramate-configurator-after-configure', onAfterConfigure);
  window.addEventListener('paramate-configurator-after-restore', onAfterRestore);
  window.addEventListener('as-session-restore-failed', onRestoreFailed);
  window.addEventListener('as-session-lost', onSessionLost);
  window.addEventListener('as-step-changed', onStepChanged);
  window.addEventListener('as-history-changed', onHistoryChanged);
  window.addEventListener('as-export-status', onExportStatus);
  // The project events are dispatched on the as-app element. They bubble, but
  // as-app is usually the top of the tree, so listen on it directly.
  appEl.addEventListener('as-before-project-loaded', onBeforeProjectLoaded);
  appEl.addEventListener('as-after-project-loaded', onAfterProjectLoaded);

  // Only what the element itself knows. appId, the current project and the
  // dirty flag are already in internalData and reach the view on their own —
  // there is nothing to copy across.
  _setSysData({
    appVersion: appEl.getAttribute('app-version') ?? undefined,
    componentsVersion: (appEl as any).version,
  });

  _teardown = () => {
    window.removeEventListener('paramate-configurator-before-configure', onBeforeConfigure);
    window.removeEventListener('paramate-configurator-after-configure', onAfterConfigure);
    window.removeEventListener('paramate-configurator-after-restore', onAfterRestore);
    window.removeEventListener('as-session-restore-failed', onRestoreFailed);
    window.removeEventListener('as-session-lost', onSessionLost);
    window.removeEventListener('as-step-changed', onStepChanged);
    window.removeEventListener('as-history-changed', onHistoryChanged);
    window.removeEventListener('as-export-status', onExportStatus);
    appEl.removeEventListener('as-before-project-loaded', onBeforeProjectLoaded);
    appEl.removeEventListener('as-after-project-loaded', onAfterProjectLoaded);
    _teardown = null;
  };
  return _teardown;
}
